
export const itemKey = (orderItemId, kitId) => `${orderItemId}:${kitId == null ? '' : kitId}`;

const isChamp = box => box && box.source === 'box_champ';

const unitsOf = entry => {
  const scanned = Number(entry.scanned_qty) || 0;
  if (entry.qty_remaining != null) {
    return {scanned, qty: scanned + (Number(entry.qty_remaining) || 0)};
  }
  if (entry.qty != null) return {scanned, qty: Number(entry.qty) || 0};
  return {scanned, qty: Number(entry.total_qty) || 0};
};

export function itemProgressFromItems(items) {
  const out = {};
  const record = (key, units) => {
    const prev = out[key];
    out[key] = prev
      ? {scanned: Math.max(prev.scanned, units.scanned), qty: Math.max(prev.qty, units.qty)}
      : units;
  };
  (Array.isArray(items) ? items : []).forEach(item => {
    if (!item || item.order_item_id == null) return;
    record(itemKey(item.order_item_id, item.kit_product_id == null ? null : item.kit_product_id), unitsOf(item));
    (Array.isArray(item.child_items) ? item.child_items : []).forEach(child => {
      if (!child || child.kit_product_id == null) return;
      record(itemKey(item.order_item_id, child.kit_product_id), unitsOf(child));
    });
  });
  return out;
}

export function buildProjection({boxes, orderItemBoxes, itemProgress} = {}) {
  const champBoxes = (Array.isArray(boxes) ? boxes : [])
    .filter(isChamp)
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  const champIds = new Set(champBoxes.map(b => b.id));
  const rows = (Array.isArray(orderItemBoxes) ? orderItemBoxes : [])
    .filter(r => r && champIds.has(r.box_id));

  const boxPosition = {};
  champBoxes.forEach(b => { boxPosition[b.id] = b.position || 0; });

  const rowsByItem = {};
  rows
    .slice()
    .sort((a, b) => {
      const bp = (boxPosition[a.box_id] || 0) - (boxPosition[b.box_id] || 0);
      if (bp !== 0) return bp;
      const rp = (a.position || 0) - (b.position || 0);
      return rp !== 0 ? rp : (a.id || 0) - (b.id || 0);
    })
    .forEach(r => {
      const key = itemKey(r.order_item_id, r.kit_id);
      (rowsByItem[key] = rowsByItem[key] || []).push(r);
    });

  const computed = {};
  const plannedKeys = new Set(Object.keys(rowsByItem));
  Object.keys(rowsByItem).forEach(key => {
    const itemRows = rowsByItem[key];
    const plannedTotal = itemRows.reduce((sum, r) => sum + (Number(r.item_qty) || 0), 0);
    const local = itemProgress && itemProgress[key];
    const achievable = local ? Math.min(Number(local.qty) || 0, plannedTotal) : plannedTotal;
    const scanned = local
      ? Number(local.scanned) || 0
      : itemRows.reduce((sum, r) => sum + (Number(r.scanned_qty) || 0), 0);

    let targetRemaining = achievable;
    let scannedRemaining = Math.min(scanned, achievable);
    itemRows.forEach(r => {
      const target = Math.min(Number(r.item_qty) || 0, targetRemaining);
      targetRemaining -= target;
      const filled = Math.min(target, scannedRemaining);
      scannedRemaining -= filled;
      computed[r.id] = {target, filled};
    });
  });

  const projectionBoxes = champBoxes.map(b => {
    const boxRows = rows
      .filter(r => r.box_id === b.id)
      .sort((a, x) => ((a.position || 0) - (x.position || 0)) || ((a.id || 0) - (x.id || 0)))
      .map(r => ({
        row: r,
        target: computed[r.id] ? computed[r.id].target : 0,
        filled: computed[r.id] ? computed[r.id].filled : 0,
      }));
    const target = boxRows.reduce((s, r) => s + r.target, 0);
    const filled = boxRows.reduce((s, r) => s + r.filled, 0);
    return {
      box: b,
      rows: boxRows,
      planned: boxRows.reduce((s, r) => s + (Number(r.row.item_qty) || 0), 0),
      target,
      filled,
      complete: filled >= target,
      sealed: Boolean(b.completed_at),
    };
  });

  const activeEntry = projectionBoxes.find(e => !e.complete) || null;
  const handoffEntry = projectionBoxes.find(e => e.target > 0 && e.complete && !e.sealed) || null;

  const overflowKeys = itemProgress
    ? Object.keys(itemProgress).filter(key => !plannedKeys.has(key))
    : [];

  return {
    boxes: projectionBoxes,
    totalBoxes: projectionBoxes.length,
    activeBoxId: activeEntry ? activeEntry.box.id : null,
    activeIndex: activeEntry ? projectionBoxes.indexOf(activeEntry) : -1,
    handoffBox: handoffEntry ? handoffEntry.box : null,
    allComplete: projectionBoxes.length > 0 && projectionBoxes.every(e => e.complete),
    overflowKeys,
  };
}
